import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ActionIcon,
  Popover,
  Box,
  Button,
  Checkbox,
  FileButton,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { notifyError, notifyWarning } from "../../../utils/notify";
import {
  IconInfoCircle,
  IconSearch,
  IconTrash,
  IconUpload,
} from "@tabler/icons-react";
import { useClient } from "../../../context/ClientContext";
import { useClientGroups } from "../../../hooks/useClientGroups";
import { parseCSV } from "../../../utils/csvUtils";
import { matchClientsByName } from "./csvMembers";
import {
  ClientGroup,
  ClientGroupMemberClient,
} from "../../../types/clientGroups";
import { SectionLabel } from "./sectionLabel";

interface PickableClient {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string | null;
}

interface Props {
  opened: boolean;
  group: ClientGroup | null;
  onClose: () => void;
  onChanged: (memberCount: number) => void;
}

/**
 * Manage Members (design §5.5). Members list with per-row removal + an
 * ADD CLIENTS section (search picker, Add Selected, CSV import matching on
 * client name). Edits are STAGED: a client added this session then removed
 * just drops from the pending adds; an existing member removed is recorded as a
 * removal; re-adding a removed client clears the removal. Everything commits on
 * Done; closing via the ✕ discards.
 */
export default function ManageMembersModal({
  opened,
  group,
  onClose,
  onChanged,
}: Props) {
  const { allClients } = useClient() as { allClients: PickableClient[] };
  const { getMembers, addMembers, removeMembers } = useClientGroups();

  const [members, setMembers] = useState<ClientGroupMemberClient[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  // Staged edits, committed on Done.
  const [pendingAdd, setPendingAdd] = useState<Set<string>>(new Set());
  const [pendingRemove, setPendingRemove] = useState<Set<string>>(new Set());
  const resetFileRef = useRef<() => void>(null);

  const refresh = useCallback(async () => {
    if (!group) return;
    const res = await getMembers(group.id);
    setMembers(res.clients);
    setTotal(res.total);
  }, [group, getMembers]);

  useEffect(() => {
    if (opened && group) {
      setPendingAdd(new Set());
      setPendingRemove(new Set());
      setSearch("");
      refresh();
    }
  }, [opened, group, refresh]);

  const byId = useMemo(() => {
    const m = new Map<string, PickableClient>();
    (allClients || []).forEach((c) => m.set(c.id, c));
    return m;
  }, [allClients]);

  const name = (c?: { first_name?: string; last_name?: string }) =>
    `${c?.first_name ?? ""} ${c?.last_name ?? ""}`.trim() || "—";

  // The list shows current members minus staged removals, plus staged adds.
  const displayed = useMemo(() => {
    const existing = members
      .filter((m) => !pendingRemove.has(m.id))
      .map((m) => ({
        id: m.id,
        first_name: m.first_name,
        last_name: m.last_name,
        email: m.email,
      }));
    const added = Array.from(pendingAdd).map((id) => {
      const c = byId.get(id);
      return {
        id,
        first_name: c?.first_name ?? "",
        last_name: c?.last_name ?? "",
        email: c?.email ?? null,
      };
    });
    return [...existing, ...added];
  }, [members, pendingRemove, pendingAdd, byId]);

  const displayedIds = useMemo(
    () => new Set(displayed.map((c) => c.id)),
    [displayed],
  );

  const candidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (allClients || [])
      .filter((c) => !displayedIds.has(c.id))
      .filter((c) => {
        if (!q) return true;
        const n = `${c.first_name ?? ""} ${c.last_name ?? ""}`.toLowerCase();
        return n.includes(q) || (c.email ?? "").toLowerCase().includes(q);
      })
      .slice(0, 200);
  }, [allClients, displayedIds, search]);

  const isExistingMember = (id: string) => members.some((m) => m.id === id);

  const stageAdd = (id: string) => {
    setPendingRemove((r) => {
      const next = new Set(r);
      next.delete(id); // re-adding a removed member clears the removal
      return next;
    });
    if (!isExistingMember(id)) setPendingAdd((a) => new Set(a).add(id));
  };

  const stageRemove = (id: string) => {
    if (pendingAdd.has(id)) {
      // added this session → just drop from pending adds
      setPendingAdd((a) => {
        const next = new Set(a);
        next.delete(id);
        return next;
      });
    } else {
      setPendingRemove((r) => new Set(r).add(id));
    }
  };

  const dirty = pendingAdd.size > 0 || pendingRemove.size > 0;

  const onCsv = async (file: File | null) => {
    if (!file) return;
    try {
      const text = await file.text();
      const { headers, records } = parseCSV(text);
      const { matchedIds, unmatched } = matchClientsByName(
        headers,
        records,
        allClients || [],
      );
      matchedIds.forEach(stageAdd);
      notifications.show({
        color: unmatched.length
          ? "var(--color-warning)"
          : "var(--color-success)",
        message: `Staged ${matchedIds.length} client(s)${
          unmatched.length
            ? `; ${unmatched.length} name(s) not found and skipped`
            : ""
        }.`,
      });
    } catch {
      notifyError("Could not read the CSV.");
    } finally {
      resetFileRef.current?.(); // allow re-importing the same file
    }
  };

  const applyAndClose = async () => {
    if (!group || !dirty) {
      onClose();
      return;
    }
    setBusy(true);
    try {
      const removes = Array.from(pendingRemove);
      const adds = Array.from(pendingAdd);
      let count = total;
      let unmatched = 0;
      if (removes.length) {
        const r = await removeMembers(group.id, removes);
        count = r.member_count;
      }
      if (adds.length) {
        const r = await addMembers(group.id, adds);
        count = r.member_count;
        unmatched = r.unmatched?.length ?? 0;
      }
      onChanged(count);
      if (unmatched)
        notifyWarning(`${unmatched} client(s) could not be added.`);
      onClose();
    } catch (e: any) {
      notifyError(e?.message || "Could not save members");
      // A removal may have committed before an add failed — re-sync from the
      // server so the modal + list reflect the real membership, and clear the
      // now-inconsistent staged edits.
      try {
        const res = await getMembers(group.id);
        setMembers(res.clients);
        setTotal(res.total);
        setPendingAdd(new Set());
        setPendingRemove(new Set());
        onChanged(res.total);
      } catch {
        /* leave the toast as the signal; nothing else we can do */
      }
    } finally {
      setBusy(false);
    }
  };

  const discardAndClose = () => {
    setPendingAdd(new Set());
    setPendingRemove(new Set());
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={discardAndClose}
      size={620}
      title={
        <Box>
          <Text fw={700} size="17px">
            Manage Members
          </Text>
          <Text size="13px" c="dimmed">
            {group?.name} — membership is managed by hand.
          </Text>
        </Box>
      }
    >
      <Stack gap="lg">
        <Box>
          <SectionLabel
            label="Members"
            right={
              total > members.length ? (
                <Text size="12.5px" c="dimmed">
                  Showing {members.length} of {total}
                </Text>
              ) : undefined
            }
          />
          <Stack gap={0} mt={6}>
            {displayed.length === 0 ? (
              <Text size="13px" c="dimmed" py="sm">
                No members yet.
              </Text>
            ) : (
              displayed.map((m) => (
                <Group
                  key={m.id}
                  justify="space-between"
                  wrap="nowrap"
                  h={44}
                  px={4}
                  style={{ borderBottom: "1px solid #ededed" }}
                >
                  <Text size="13px" fw={500} lineClamp={1}>
                    {name(m)}
                    {pendingAdd.has(m.id) && (
                      <Text span size="11px" c="var(--brand-teal)" ml={6}>
                        pending
                      </Text>
                    )}
                  </Text>
                  <Group gap="sm" wrap="nowrap">
                    <Text size="13px" c="dimmed" lineClamp={1} maw={220}>
                      {m.email}
                    </Text>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      aria-label="Remove From Group"
                      title="Remove From Group"
                      onClick={() => stageRemove(m.id)}
                    >
                      <IconTrash size={15} />
                    </ActionIcon>
                  </Group>
                </Group>
              ))
            )}
          </Stack>
        </Box>

        <Box>
          <Group justify="space-between" align="center" mb={6}>
            <SectionLabel label="Add Clients" />
            <Group gap="xs">
              <FileButton
                resetRef={resetFileRef}
                accept=".csv,text/csv"
                onChange={(file) => onCsv(file)}
              >
                {(props) => (
                  <Button
                    {...props}
                    size="xs"
                    variant="default"
                    leftSection={<IconUpload size={14} />}
                  >
                    Import CSV
                  </Button>
                )}
              </FileButton>
              <Popover width={300} position="bottom-end" withArrow shadow="md">
                <Popover.Target>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    aria-label="CSV format help"
                  >
                    <IconInfoCircle size={16} />
                  </ActionIcon>
                </Popover.Target>
                <Popover.Dropdown>
                  <Text size="12.5px" fw={600} mb={4}>
                    CSV format
                  </Text>
                  <Text size="12.5px" c="dimmed" mb={6}>
                    A header row with <b>first_name</b> and <b>last_name</b>{" "}
                    columns, then one client per row.
                  </Text>
                  <Box
                    style={{
                      fontFamily: "monospace",
                      fontSize: 11.5,
                      background: "#f4f4f4",
                      border: "1px solid #e6e6e6",
                      borderRadius: 6,
                      padding: "6px 8px",
                      whiteSpace: "pre",
                      overflowX: "auto",
                    }}
                  >
                    {"first_name,last_name\nJane,Doe\nJohn,Smith"}
                  </Box>
                  <Text size="11.5px" c="dimmed" mt={6}>
                    Names are matched to this location&apos;s clients. A name
                    that matches more than one client adds all of them; a name
                    with no match is reported and skipped.
                  </Text>
                </Popover.Dropdown>
              </Popover>
            </Group>
          </Group>
          <TextInput
            size="xs"
            placeholder="Search clients"
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            mb={8}
            maw={320}
          />
          <Box
            style={{
              border: "1px solid #e6e6e6",
              borderRadius: 12,
              maxHeight: 260,
              overflow: "auto",
            }}
          >
            {candidates.map((c) => (
              <Group
                key={c.id}
                wrap="nowrap"
                h={44}
                px={14}
                gap={12}
                style={{
                  borderBottom: "1px solid #ededed",
                  cursor: "pointer",
                }}
                onClick={() => stageAdd(c.id)}
              >
                <Checkbox checked={false} readOnly tabIndex={-1} />
                <Text size="13px" fw={500} style={{ flex: 1 }} lineClamp={1}>
                  {name(c)}
                </Text>
                <Text size="13px" c="dimmed" lineClamp={1} maw={220}>
                  {c.email}
                </Text>
              </Group>
            ))}
            {candidates.length === 0 && (
              <Text size="13px" c="dimmed" p="sm">
                No clients match.
              </Text>
            )}
          </Box>
          <Text size="12.5px" c="dimmed" mt={9}>
            A CSV import matches on client name; rows with no match are reported
            and skipped.
          </Text>
        </Box>

        <Group justify="flex-end">
          <Button
            color="var(--brand-teal)"
            loading={busy}
            onClick={applyAndClose}
          >
            Done
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
